import React from 'react'
import { useQuery } from '@apollo/client'
import { Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import KeyFigureCard, { KeyFigureUnit } from './components/KeyFigureCard'
import Section from './components/Section'
import { OPERATION_PRODUCT_ANALYTICS_KEY_FIGURES } from './operations-gql/queries'
import { PALETTE } from './styles'
import {
  KeyFigures as KeyFiguresData,
  KeyFiguresQuery,
  ProductAnalyticsPeriod,
  ProductAnalyticsPlatform,
  ProductAnalyticsVariables,
} from './types'

type KeyFiguresProps = {
  period: ProductAnalyticsPeriod
  platform: ProductAnalyticsPlatform
}

type KeyFigureConfig = {
  key: keyof KeyFiguresData
  labelKey: string
  mobileLabelKey?: string
  unit: KeyFigureUnit
}

const KEY_FIGURES: KeyFigureConfig[] = [
  { key: 'uniqueVisitorsPerDay', labelKey: 'unique_visitors_per_day', unit: 'number' },
  { key: 'sessionsPerDay', labelKey: 'sessions_per_day', unit: 'number' },
  { key: 'medianSessionDuration', labelKey: 'median_session_duration', unit: 'duration' },
  {
    key: 'medianSectionsPerSession',
    labelKey: 'median_sections_per_session',
    mobileLabelKey: 'median_screens_per_session',
    unit: 'number',
  },
  { key: 'generatedCodes', labelKey: 'generated_codes', unit: 'number' },
]

const KeyFigures: React.FC<KeyFiguresProps> = ({ period, platform }) => {
  const { t } = useTranslation()
  const { data, loading, error } = useQuery<KeyFiguresQuery, ProductAnalyticsVariables>(
    OPERATION_PRODUCT_ANALYTICS_KEY_FIGURES,
    { variables: { period, platform }, errorPolicy: 'all' }
  )
  const keyFigures = data?.productAnalytics?.keyFigures

  const errorNotice = error && !keyFigures && !loading && (
    <Typography role="alert" sx={{ fontSize: 12, color: PALETTE.error }}>
      {t('stats.product_analytics.states.error')}
    </Typography>
  )

  return (
    <Section
      id="product-analytics-key-figures"
      title={t('stats.product_analytics.key_figures.title')}
      columns={{ xs: 2, sm: 3, md: 5 }}
      notice={errorNotice}
    >
      {KEY_FIGURES.map(({ key, labelKey, mobileLabelKey, unit }) => (
      <KeyFigureCard
        key={key}
        label={t(`stats.product_analytics.key_figures.${platform === 'MOBILE' && mobileLabelKey ? mobileLabelKey : labelKey}`)}
        value={keyFigures?.[key]}
        unit={unit}
        loading={loading}
      />
      ))}
    </Section>
  )
}

export default KeyFigures
